import React from 'react';
import { UserPlus } from 'lucide-react';
import NimGamePage from '../nimGamePage';
import CodeQuizGamePage from '../codeQuizGamePage';
import useGamePage from '../../../../hooks/useGamePage';
import { GameInstance, NimGameState } from '../../../../types/types';
import { Button } from '../../../ui/button';
import { Alert, AlertDescription } from '../../../ui/alert';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../../common/BreadcrumbNav';
import InviteUserDialog from '../InviteUserDialog';
import useUserContext from '../../../../hooks/useUserContext';

/**
 * Component to display the game page for a specific game type, including controls and game state.
 * @returns A React component rendering:
 * - A header with the game title and current game status.
 * - A "Leave Game" button to exit the current game.
 * - The game component specific to the game type (e.g., `NimGamePage` for "Nim").
 * - An error message if an error occurs during the game.
 */
const GamePage = () => {
  const { gameInstance, error, handleLeaveGame } = useGamePage();
  const { user } = useUserContext();

  /**
   * Renders the appropriate game component based on the game type.
   * @param gameType The type of the game to render (e.g., "Nim").
   * @returns A React component corresponding to the specified game type, or a
   * fallback message for unknown types.
   */
  const renderGameComponent = (gameType: string) => {
    if (!gameInstance) return null;

    switch (gameType) {
      case 'Nim':
        return <NimGamePage gameInstance={gameInstance as GameInstance<NimGameState>} />;
      case 'CodeQuiz':
        return <CodeQuizGamePage gameId={gameInstance.gameID} />;
      default:
        return <div className='p-4 bg-gray-100 rounded-md'>Unknown game type</div>;
    }
  };

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Games', href: '/games' },
    { label: gameInstance ? `${gameInstance.gameType} Game` : 'Game' },
  ];

  return (
    <div className='container mx-auto p-6 max-w-4xl bg-white rounded-lg shadow-md'>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbItems} />

      <header className='border-b pb-4 mb-6'>
        <h1 className='text-2xl font-bold text-gray-800'>
          {gameInstance ? `${gameInstance.gameType} Game` : 'Game'}
        </h1>
        <p className='text-gray-600 mt-1'>
          Status: {gameInstance ? gameInstance.state.status : 'Not started'}
        </p>
      </header>

      <div className='mb-6 flex gap-2'>
        <Button variant='destructive' onClick={handleLeaveGame} className='hover:bg-red-600'>
          Leave Game
        </Button>

        {gameInstance && gameInstance.state.status === 'WAITING_TO_START' && (
          <InviteUserDialog gameID={gameInstance.gameID} currentUsername={user.username}>
            <Button variant='outline' className='flex items-center gap-1'>
              <UserPlus className='w-4 h-4' />
              Invite Player
            </Button>
          </InviteUserDialog>
        )}
      </div>

      {gameInstance && renderGameComponent(gameInstance.gameType)}

      {error && (
        <Alert variant='destructive' className='mt-6'>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default GamePage;
